import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import adminService from '../services/adminService';

export const useDashboardStatsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'dashboardStats', params],
    queryFn: () => adminService.getDashboardStats(params),
    ...options,
  });
};

export const useDashboardOverviewQuery = (month, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'dashboardOverview', month],
    queryFn: () => adminService.getDashboardOverview(month),
    ...options,
  });
};

export const useRevenuePerformanceQuery = (days = 7, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'revenuePerformance', days],
    queryFn: () => adminService.getRevenuePerformance(days),
    ...options,
  });
};

export const useFinancialSummaryQuery = (options = {}) => {
  return useQuery({
    queryKey: ['admin', 'financialSummary'],
    queryFn: () => adminService.getFinancialSummary(),
    ...options,
  });
};

export const useRevenueTrendsQuery = (options = {}) => {
  return useQuery({
    queryKey: ['admin', 'revenueTrends'],
    queryFn: () => adminService.getRevenueTrends(),
    ...options,
  });
};

export const useDashboardTransactionsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'transactions', params],
    queryFn: () => adminService.getDashboardTransactions(params),
    ...options,
  });
};

export const useWithdrawalsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'withdrawals', params],
    queryFn: () => adminService.getWithdrawals(params),
    ...options,
  });
};

// Every payout action reloads the queue: an approved or declined request must
// not linger in the list an admin is working through.
const useWithdrawalMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'withdrawals'] });
    },
  });
};

export const useApproveWithdrawalMutation = () =>
  useWithdrawalMutation((withdrawalId) => adminService.approveWithdrawal(withdrawalId));

export const useMarkWithdrawalPaidMutation = () =>
  useWithdrawalMutation(({ withdrawalId, paymentReference }) =>
    adminService.markWithdrawalPaid(withdrawalId, paymentReference)
  );

export const useDeclineWithdrawalMutation = () =>
  useWithdrawalMutation(({ withdrawalId, reason }) =>
    adminService.declineWithdrawal(withdrawalId, reason)
  );

export const useRevertWithdrawalMutation = () =>
  useWithdrawalMutation(({ withdrawalId, reason }) =>
    adminService.revertWithdrawal(withdrawalId, reason)
  );

export const useRefundsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'refunds', params],
    queryFn: () => adminService.getRefunds(params),
    ...options,
  });
};

const useRefundMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'refunds'] });
    },
  });
};

export const useApproveRefundMutation = () =>
  useRefundMutation((refundId) => adminService.approveRefund(refundId));

export const useMarkRefundPaidMutation = () =>
  useRefundMutation(({ refundId, note }) => adminService.markRefundPaid(refundId, note));

export const useDeclineRefundMutation = () =>
  useRefundMutation(({ refundId, reason }) => adminService.declineRefund(refundId, reason));

export const useRevertRefundMutation = () =>
  useRefundMutation(({ refundId, reason }) => adminService.revertRefund(refundId, reason));

export const useDashboardProvidersQuery = (options = {}) => {
  return useQuery({
    queryKey: ['admin', 'providers'],
    queryFn: () => adminService.getDashboardProviders(),
    ...options,
  });
};

export const useProvidersOverviewQuery = (month, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'providersOverview', month],
    queryFn: () => adminService.getProvidersOverview(month),
    ...options,
  });
};

export const useProvidersDirectoryQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'providersDirectory', params],
    queryFn: () => adminService.getProvidersDirectory(params),
    ...options,
  });
};

export const useProviderDetailsQuery = (providerId, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'providerDetails', providerId],
    queryFn: () => adminService.getProviderDetails(providerId),
    enabled: !!providerId,
    ...options,
  });
};

export const useAssistantsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'assistants', params],
    queryFn: () => adminService.getAssistants(params),
    ...options,
  });
};

export const useAssistantByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'assistants', id],
    queryFn: () => adminService.getAssistantById(id),
    enabled: !!id,
    ...options,
  });
};

export const useUpdateAssistantMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => adminService.updateAssistant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assistants'] });
    },
  });
};

export const useSettingsQuery = (options = {}) => {
  return useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminService.getSettings(),
    ...options,
  });
};

export const useUpdateSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings) => adminService.updateSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
};

export const useDownloadDashboardReportQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'dashboardReport', params],
    queryFn: () => adminService.downloadDashboardReport(params),
    enabled: false,
    ...options,
  });
};

export const useDownloadProvidersInsightsQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'providersInsights', params],
    queryFn: () => adminService.downloadProvidersInsights(params),
    enabled: false,
    ...options,
  });
};

export const useReviewProviderProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, payload }) => adminService.reviewProviderProfile(providerId, payload),
    onSuccess: (data, { providerId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assistants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providerDetails', providerId] });
    },
  });
};

export const useReviewProviderChangesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ providerId, payload }) =>
      adminService.reviewProviderChanges(providerId, payload),
    onSuccess: (data, { providerId }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assistants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providerDetails', providerId] });
    },
  });
};

export const useReleaseBookingPayoutMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, payload = {} }) =>
      adminService.releaseBookingPayout(bookingId, payload),
    onSuccess: () => {
      // The booking closes and the assistant's wallet moves, so anything
      // showing either has to be refetched.
      queryClient.invalidateQueries({ queryKey: ['admin', 'userDetail'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providerDetails'] });
    },
  });
};

export const useSuspendProviderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (providerId) => adminService.suspendProvider(providerId),
    onSuccess: (data, providerId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assistants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providerDetails', providerId] });
    },
  });
};

export const useUnsuspendProviderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (providerId) => adminService.unsuspendProvider(providerId),
    onSuccess: (data, providerId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assistants'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'providerDetails', providerId] });
    },
  });
};

export const useDeleteProviderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (providerId) => adminService.deleteProvider(providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'assistants'] });
    },
  });
};

export const useAdminUsersQuery = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'users', params],
    queryFn: () => adminService.getUsers(params),
    ...options,
  });
};

export const useAdminUserDetailQuery = (userId, options = {}) => {
  return useQuery({
    queryKey: ['admin', 'userDetail', userId],
    queryFn: () => adminService.getUserById(userId),
    enabled: !!userId,
    ...options,
  });
};

export const useSuspendUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId) => adminService.suspendUser(userId),
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'userDetail', userId] });
    },
  });
};

export const useUnsuspendUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId) => adminService.unsuspendUser(userId),
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'userDetail', userId] });
    },
  });
};
